import type { SupplyTableResponse } from '@/services/supplyService';
import {
  onSupplyTableCacheInvalidated,
  tryReadActionableFromCache,
  tryReadMergedFromCache,
  writeActionableToCache,
  writeMergedToCache,
} from '@/utils/supplyTableCache';
import { fetchAllSupplyTablePages, waitForSupplyCatalogIdle } from '@/utils/fetchSupplyTablePages';
import { readSupplyApplyMoqMpFromSession } from '@/utils/supplyKpiAlignment';

const prefetchControllers = new Map<number, AbortController>();

/**
 * Precarga la tabla de supply en segundo plano (misma estrategia que SupplyPage):
 * qty>0 primero (actionable), idle corto, luego catálogo completo (`merged` / dump).
 */
export function prefetchSupplyTable(warehouseId: number): void {
  if (!Number.isFinite(warehouseId)) return;
  if (tryReadMergedFromCache(warehouseId)) return;

  const existing = prefetchControllers.get(warehouseId);
  if (existing) {
    existing.abort();
  }
  const controller = new AbortController();
  prefetchControllers.set(warehouseId, controller);
  const signal = controller.signal;

  void (async () => {
    try {
      // Una sola foto en caché; MP/MOQ se aplica en cliente al pintar.
      const applyMoqMpPrefetch = readSupplyApplyMoqMpFromSession();
      if (!tryReadActionableFromCache(warehouseId)) {
        const actionable = await fetchAllSupplyTablePages(
          {
            warehouse_id: warehouseId,
            needs_reorder: false,
            suggested_greater_zero: true,
            apply_moq_mp: applyMoqMpPrefetch,
            with_rules: false,
          },
          { signal },
        );
        if (signal.aborted || !actionable) return;
        writeActionableToCache(warehouseId, actionable);
      }

      await waitForSupplyCatalogIdle(undefined, signal);
      if (signal.aborted) return;

      const fullCatalog: SupplyTableResponse | null = await fetchAllSupplyTablePages(
        {
          warehouse_id: warehouseId,
          needs_reorder: false,
          suggested_greater_zero: false,
          apply_moq_mp: applyMoqMpPrefetch,
          with_rules: false,
        },
        { signal },
      );
      if (signal.aborted || !fullCatalog) return;
      writeMergedToCache(warehouseId, fullCatalog);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.warn('[supplyPrefetch] background load failed:', error);
    } finally {
      if (prefetchControllers.get(warehouseId) === controller) {
        prefetchControllers.delete(warehouseId);
      }
    }
  })();
}

export function cancelSupplyTablePrefetch(warehouseId?: number): void {
  if (warehouseId != null) {
    prefetchControllers.get(warehouseId)?.abort();
    prefetchControllers.delete(warehouseId);
    return;
  }
  prefetchControllers.forEach((c) => c.abort());
  prefetchControllers.clear();
}

onSupplyTableCacheInvalidated((warehouseId) => {
  cancelSupplyTablePrefetch(warehouseId);
});
